import React from 'react';
import Button from '@material-ui/core/Button';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePreparePurchase, usePurchase } from '../../hooks/api/purchasesApi';
import LoadingIndicator from '../../../../components/LoadingIndicator/LoadingIndicator';
import { CURRENCY_ID_CZK, formatVolume, useFormatCurrency, useFormatPriceForMWh } from '../../../../utils/formatters';
import { FONT_MAIN } from '../../../../constants/fonts';
import { transactionTypeDetermination } from '../../utils/transactionType';

const DialogLayout = styled.div`
  min-height: 300px;
  padding: 32px;
  font-family: "${FONT_MAIN}";
  color: rgba(0,0,0,0.87);
  display: flex;
  flex-direction: column;
  .information {
    flex: 1;
    .header {
      font-size: 21.97px;
      letter-spacing: 0.25px;
      line-height: 27px;
      margin-bottom: 40px;
    }
    .spaceBeforeTarrifs {
      height: 21px;
    }
    .row {
      display: flex;
      width: 100%;
      .label {
        flex:1;
        color: rgba(0,0,0,0.6);
        font-size: 15.38px;
        letter-spacing: 0.25px;
        line-height: 20px;
      }
      .value {
        flex: 1;
        font-size: 15.38px;
        font-weight: 500;
        letter-spacing: 0.25px;
        line-height: 20px;
      }
    }
  }
  .dialogFooter {
    width: 100%;
    display: flex;
    flex-align: right;
    align-items: center;
    justify-content: flex-end;
    margin-top: 24px;
    align-self: end;
    > button {
      margin-left: 7px;
      min-width: 87px;
    }
  }
  @media all and (width > 600px) {
    min-width: 470px;
  }
`;

const DialogLabelValue = ({ intlDialogId, value, intlValues }) => {
  const DIALOG_INTL_PREFIX = 'app.contract.buy.dialog';
  return (
    <div className={'row'}>
      <div className={'label'}>
        <FormattedMessage id={`${DIALOG_INTL_PREFIX}.${intlDialogId}`} values={intlValues} />
      </div>
      <div className={'value'}>{value}</div>
    </div>
  );
};
DialogLabelValue.propTypes = {
  intlDialogId: PropTypes.string,
  value: PropTypes.string,
  intlValues: PropTypes.object,
};

const reverseQuantities = (purchaseInfo) => {
  const quantities = purchaseInfo.buyingProduct.quantities.map((quantityObj) => {
    return {
      ...quantityObj,
      quantity: quantityObj.quantity * -1,
    };
  });
  return {
    ...purchaseInfo,
    buyingProduct: {
      ...purchaseInfo.buyingProduct,
      quantities,
    },
  };
};

export const BuyDialogBody = ({ onClose, contract, product, releasing, manualPrice, manualRate, purchaseInfo, onError, onBuy, sendNotification }) => {
  const { isFix } = contract;
  const transactionType = transactionTypeDetermination(isFix, releasing);
  const DIALOG_INTL_PREFIX = `app.contract.${transactionType.toLowerCase()}.dialog`;
  const intl = useIntl();
  const purchaseInfoPreprocessed = releasing ? reverseQuantities(purchaseInfo) : purchaseInfo;
  const { purchasePrepare, error } = usePreparePurchase(contract, product, purchaseInfoPreprocessed, manualPrice, manualRate, releasing);
  const { purchase, purchaseSubmitting, purchaseError, purchaseResult } = usePurchase(
    contract,
    product,
    manualPrice,
    manualRate,
    purchaseInfoPreprocessed,
    purchasePrepare,
    releasing,
    sendNotification,
  );
  if (purchaseResult) {
    onBuy(purchaseResult);
  }

  const currencyId = contract.currency.id;

  const formatCurrency = useFormatCurrency(currencyId);
  const formatPriceForMWh = useFormatPriceForMWh(currencyId);

  if (error || purchaseError) {
    onError();
  }
  const isLoading = !purchasePrepare || purchaseSubmitting;

  const productPriceObj = releasing ? product.salePrice : product.price;
  const priceSource = productPriceObj.priceSource;

  let purchaseIntlId;
  switch (priceSource) {
    case 'BESTASK':
      purchaseIntlId = 'priceBestAsk';
      break;
    case 'BESTBID':
      purchaseIntlId = 'priceBestBid';
      break;
    default:
      purchaseIntlId = 'priceSettlement';
      break;
  }

  return (
    <DialogLayout>
      <div className={'information'}>
        <div className={'header'}>
          <FormattedMessage id={`${DIALOG_INTL_PREFIX}.header`} />
        </div>
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <>
            <DialogLabelValue intlDialogId={'product'} value={purchasePrepare.exchangeProduct.name} />
            <DialogLabelValue intlDialogId={'volume'} value={formatVolume(intl, purchaseInfo.buyTotalsInfo.volumeMWh)} />
            <DialogLabelValue intlDialogId={purchaseIntlId} value={formatPriceForMWh(manualPrice || purchasePrepare.price)} />
            {currencyId === CURRENCY_ID_CZK && <DialogLabelValue intlDialogId={'exchangeRateEur'} value={formatCurrency(manualRate || purchasePrepare.rate)} />}

            <div className={'spaceBeforeTarrifs'} />
            {purchasePrepare.tariffPrices.map((tariffPrice, index) => {
              return <DialogLabelValue key={index} intlDialogId={'tariff'} intlValues={{ index: index + 1 }} value={formatCurrency(tariffPrice.value)} />;
            })}
          </>
        )}
      </div>
      <div className={'dialogFooter'}>
        <Button onClick={onClose} variant={'text'} color={'primary'}>
          <FormattedMessage id={`${DIALOG_INTL_PREFIX}.cancelButton`} />
        </Button>
        <Button
          onClick={() => {
            purchase();
          }}
          variant={'contained'}
          disabled={isLoading}
          color={'primary'}>
          <FormattedMessage id={`${DIALOG_INTL_PREFIX}.tradeButton`} />
        </Button>
      </div>
    </DialogLayout>
  );
};

BuyDialogBody.propTypes = {
  onClose: PropTypes.func.isRequired,
  contract: PropTypes.object,
  product: PropTypes.object,
  releasing: PropTypes.bool,
  manualPrice: PropTypes.number,
  manualRate: PropTypes.number,
  purchaseInfo: PropTypes.object,
  onError: PropTypes.func,
  sendNotification: PropTypes.bool,
  onBuy: PropTypes.func.isRequired,
};
